import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  Loader2,
  Rocket,
  FileText,
  AlertCircle,
  CheckCircle2,
  CalendarClock,
  Send,
  Tag,
  Plus,
  Sparkles,
} from "lucide-react";
import { CONTENT_STATUS_STYLES } from "./constants";
import LaunchCampaignDialog from "./LaunchCampaignDialog";
import AiStatusPanel from "./AiStatusPanel";
import { PrepareWeekCard } from "./PrepareWeekCard";
import { motion } from "framer-motion";

const COUNTS = [
  { key: "active_campaigns", label: "Active Campaigns", icon: Rocket, tint: "bg-emerald-50 text-emerald-600" },
  { key: "draft_content", label: "Draft Content", icon: FileText, tint: "bg-slate-100 text-slate-600" },
  { key: "needs_review", label: "Needs Review", icon: AlertCircle, tint: "bg-amber-50 text-amber-600" },
  { key: "approved", label: "Approved", icon: CheckCircle2, tint: "bg-[#F3E8FF] text-[#6B21A8]" },
  { key: "scheduled", label: "Scheduled", icon: CalendarClock, tint: "bg-blue-50 text-blue-600" },
  { key: "published", label: "Published", icon: Send, tint: "bg-purple-50 text-purple-600" },
];

function ActionsNeeded({ navigate }) {
  const [actions, setActions] = useState([]);
  useEffect(() => { api.get("/marketing/actions-needed").then((r) => setActions(r.data.actions || [])).catch(() => {}); }, []);
  return (
    <div className="bg-white border border-amber-200 rounded-xl p-5 mb-6" data-testid="actions-needed-panel">
      <p className="text-xs uppercase tracking-widest text-amber-600 font-semibold flex items-center gap-1.5"><AlertCircle className="h-3.5 w-3.5" /> Marketing Actions Needed</p>
      <div className="mt-3 space-y-1.5">
        {actions.length === 0 && <p className="text-sm text-gray-400" data-testid="actions-none">You're all caught up — no marketing actions needed.</p>}
        {actions.map((a, i) => (
          <button key={i} onClick={() => navigate(a.link)} className="w-full text-left text-sm text-gray-700 hover:text-[#581C87] flex items-center justify-between border-b border-gray-50 py-1.5" data-testid={`action-${a.type}`}>
            <span>{a.label}</span><span className="text-[#581C87]">→</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Overview() {
  const [data, setData] = useState(null);
  const [launchOpen, setLaunchOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/marketing/dashboard").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  if (data === null)
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-[#581C87]" />
      </div>
    );

  if (data === false)
    return <p className="text-sm text-red-600">Failed to load marketing dashboard.</p>;

  const c = data.counts;

  return (
    <div data-testid="marketing-overview">
      <LaunchCampaignDialog open={launchOpen} onOpenChange={setLaunchOpen} />
      <ActionsNeeded navigate={navigate} />

      <PrepareWeekCard />

      {/* Launch AI Campaign hero */}
      <div className="bg-gradient-to-br from-[#2D1B3D] to-[#111827] rounded-xl p-6 md:p-8 mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-purple-300 font-semibold">AI Campaign Director</p>
          <h2 className="font-heading text-2xl font-bold text-white mt-1">Enter a goal. The AI builds the whole campaign.</h2>
          <p className="text-gray-300 text-sm mt-2 max-w-xl">Strategy, themes, hooks, scripts, captions, CTAs, hashtags and platform variations — placed into your Approval Inbox for review.</p>
        </div>
        <Button onClick={() => setLaunchOpen(true)} className="bg-white text-[#581C87] hover:bg-purple-50 shrink-0 h-11 px-6" data-testid="launch-ai-campaign-button">
          <Sparkles className="h-4 w-4 mr-2" /> Launch AI Campaign
        </Button>
      </div>

      <AiStatusPanel />

      {/* Quick actions */}
      <div className="flex flex-wrap gap-3 my-8">
        <Button
          onClick={() => navigate("/marketing/offers?new=1")}
          className="bg-[#581C87] hover:bg-[#6B21A8] text-white"
          data-testid="quick-add-offer"
        >
          <Tag className="h-4 w-4 mr-2" /> Add Offer
        </Button>
        <Button
          onClick={() => navigate("/marketing/campaigns?new=1")}
          variant="outline"
          className="border-gray-200 hover:border-[#581C87] hover:text-[#581C87]"
          data-testid="quick-create-campaign"
        >
          <Rocket className="h-4 w-4 mr-2" /> Create Campaign
        </Button>
        <Button
          onClick={() => navigate("/marketing/content?new=1")}
          variant="outline"
          className="border-gray-200 hover:border-[#581C87] hover:text-[#581C87]"
          data-testid="quick-add-content"
        >
          <Plus className="h-4 w-4 mr-2" /> Add Content
        </Button>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        {COUNTS.map((item, i) => {
          const Icon = item.icon;
          return (
            <motion.div
              key={item.key}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.04 }}
              className="bg-white border border-gray-200 rounded-xl p-5"
              data-testid={`mkt-count-${item.key}`}
            >
              <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${item.tint}`}>
                <Icon className="h-4 w-4" />
              </div>
              <p className="mt-3 font-heading text-2xl font-bold">{c[item.key]}</p>
              <p className="text-xs text-gray-500 mt-1">{item.label}</p>
            </motion.div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="priority-campaign">
          <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">Priority Campaign</p>
          {data.priority_campaign ? (
            <>
              <h3 className="font-heading font-bold text-lg mt-2">{data.priority_campaign.name}</h3>
              <p className="text-sm text-gray-500 mt-1">
                {data.priority_campaign.objective || "No objective set"}
              </p>
              <span className={`inline-flex mt-3 rounded-full px-3 py-1 text-xs font-semibold ${CONTENT_STATUS_STYLES[data.priority_campaign.status] || "bg-gray-100 text-gray-600"}`}>
                {data.priority_campaign.status}
              </span>
            </>
          ) : (
            <p className="text-sm text-gray-400 mt-2">No campaigns yet.</p>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="priority-offer">
          <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">Priority Offer</p>
          {data.priority_offer ? (
            <>
              <h3 className="font-heading font-bold text-lg mt-2">{data.priority_offer.name}</h3>
              <p className="text-sm text-gray-500 mt-1">
                {data.priority_offer.offer_type} · Goal: {data.priority_offer.conversion_goal || "—"}
              </p>
              <span className="inline-flex mt-3 rounded-full px-3 py-1 text-xs font-semibold bg-[#F3E8FF] text-[#6B21A8]">
                {data.priority_offer.priority}
              </span>
            </>
          ) : (
            <p className="text-sm text-gray-400 mt-2">No offers yet.</p>
          )}
        </div>
      </div>

      {/* Recent activity */}
      <div className="bg-white border border-gray-200 rounded-xl mt-6 overflow-hidden" data-testid="recent-activity">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="font-heading font-bold">Recent Content Activity</h3>
        </div>
        {data.recent_activity.length === 0 ? (
          <div className="px-6 py-10 text-center text-gray-400 text-sm">
            No content yet. Use "Add Content" to create your first item.
          </div>
        ) : (
          data.recent_activity.map((r) => (
            <button
              key={r.id}
              onClick={() => navigate(`/marketing/content/${r.id}`)}
              className="w-full text-left flex items-center justify-between px-6 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors"
              data-testid={`recent-item-${r.id}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{r.title}</p>
                <p className="text-xs text-gray-400">{r.platform || "No platform"} · {r.content_format || "—"}</p>
              </div>
              <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${CONTENT_STATUS_STYLES[r.status] || "bg-gray-100 text-gray-600"}`}>
                {r.status}
              </span>
            </button>
          ))
        )}
      </div>

      <div className="mt-6 flex items-center gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
        <Sparkles className="h-4 w-4 text-[#581C87]" />
        End-to-end ready: <strong className="mx-1">AI generation, branded media, calendar and Buffer scheduling</strong> — auto-publishing stays off until you enable Controlled Autopilot.
      </div>
    </div>
  );
}
